import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowRight,
  Check,
  Copy,
  ExternalLink,
  Minus,
  Plus,
  ShoppingBag,
  Trash2,
  X,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { getVisitorId } from '@/lib/visitor';
import { formatMoney } from '@/lib/money';
import { formatOptions } from '@/lib/products';
import {
  PAYMENT_META,
  enabledPaymentMethods,
  payTarget,
  type PaymentConfig,
  type PaymentMethod,
} from '@/lib/payments';
import { SPRING_DEFAULT } from '@/lib/motion';
import { cn, errorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { useCart } from './cart';
import type { ThemePreset } from '@/lib/themes';

const CheckoutSchema = z.object({
  name: z.string().min(1, 'Required').max(200),
  email: z.string().email('Enter a valid email').max(320),
  phone: z.string().max(40).optional(),
  note: z.string().max(1000).optional(),
});
type CheckoutValues = z.infer<typeof CheckoutSchema>;

type Step = 'details' | 'pay' | 'done';

interface PlacedOrder {
  id: string;
  orderNumber: number;
  amountCents: number;
  method: PaymentMethod;
  email: string;
}

export interface CheckoutSheetProps {
  open: boolean;
  onClose: () => void;
  card: { id: string; display_name: string; slug: string };
  config: PaymentConfig;
  theme: ThemePreset;
  onCheckoutStart?: () => void;
  onOrderPlaced?: (order: { id: string; method: PaymentMethod; amountCents: number }) => void;
  onPaymentClaimed?: (orderId: string) => void;
}

/**
 * Bottom sheet that turns the cart into an order.
 *
 *  details → the visitor reviews lines, enters contact info, picks a payment
 *            method. Submit inserts `orders` + `order_items` (anonymous, id
 *            generated client-side because RLS blocks RETURNING) and pings
 *            /api/orders/notify so both sides get an email.
 *  pay     → method-specific pay link (Venmo / PayPal / Square) or Zelle
 *            instructions, always with the order number to put in the memo.
 *            "I've sent it" pings notify again with event=claimed.
 *  done    → confirmation.
 */
export function CheckoutSheet({
  open,
  onClose,
  card,
  config,
  theme,
  onCheckoutStart,
  onOrderPlaced,
  onPaymentClaimed,
}: CheckoutSheetProps) {
  const cart = useCart();
  const methods = enabledPaymentMethods(config);
  const currency = (config.currency ?? 'USD').toUpperCase();
  const [step, setStep] = useState<Step>('details');
  const [method, setMethod] = useState<PaymentMethod | null>(methods[0] ?? null);
  const [placed, setPlaced] = useState<PlacedOrder | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CheckoutValues>({ resolver: zodResolver(CheckoutSchema) });

  // Fresh sheet each time it opens (unless we're mid-payment — keep that).
  useEffect(() => {
    if (!open) return;
    if (step === 'pay') return;
    setStep('details');
    setPlaced(null);
    setSubmitting(false);
    if (!method || !methods.includes(method)) setMethod(methods[0] ?? null);
    onCheckoutStart?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!cart) return null;
  const lines = cart.lines;
  const subtotal = cart.subtotal;

  async function placeOrder(values: CheckoutValues) {
    if (!cart || !method || lines.length === 0) return;
    setSubmitting(true);
    try {
      // Generate the id here: anonymous inserts can't use RETURNING under
      // RLS (no public SELECT policy), and we need the id for the items and
      // the notify call.
      const orderId = crypto.randomUUID();
      const { error: orderErr } = await supabase.from('orders').insert({
        id: orderId,
        card_id: card.id,
        customer_name: values.name.trim(),
        customer_email: values.email.trim().toLowerCase(),
        customer_phone: values.phone?.trim() || null,
        note: values.note?.trim() || null,
        currency,
        subtotal_cents: subtotal,
        payment_method: method,
        visitor_id: getVisitorId() || null,
      });
      if (orderErr) throw orderErr;

      const { error: itemsErr } = await supabase.from('order_items').insert(
        lines.map((l) => ({
          order_id: orderId,
          product_link_id: l.product.id,
          name: l.product.name,
          unit_price_cents: l.product.priceCents,
          quantity: l.qty,
          image_url: l.product.imageUrl,
          options: Object.keys(l.options).length ? l.options : null,
        })),
      );
      if (itemsErr) throw itemsErr;

      // The order number comes from a DB sequence; we can't read it back
      // anonymously, so the notify endpoint returns it (service role).
      let orderNumber = 0;
      try {
        const res = await fetch('/api/orders/notify', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ order_id: orderId, event: 'created' }),
        });
        if (res.ok) {
          const body = (await res.json()) as { order_number?: number };
          if (typeof body.order_number === 'number') orderNumber = body.order_number;
        }
      } catch {
        /* email is best-effort — the order is already saved */
      }

      onOrderPlaced?.({ id: orderId, method, amountCents: subtotal });
      setPlaced({ id: orderId, orderNumber, amountCents: subtotal, method, email: values.email });
      cart.clear();
      reset();

      if (method === 'stripe') {
        // Hosted Stripe Checkout — the webhook marks the order paid and
        // Stripe sends the visitor to /o/<id>?paid=1 afterwards. If the
        // session can't be created the order still exists, so land on the
        // done step whose "Track order" link offers Pay by card again.
        try {
          const res = await fetch('/api/stripe/checkout', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ order_id: orderId }),
          });
          const out = (await res.json().catch(() => ({}))) as { url?: string; error?: string };
          if (!res.ok || !out.url) throw new Error(out.error ?? 'Could not open card checkout.');
          window.location.href = out.url;
          return;
        } catch (e) {
          toast({
            title: 'Card checkout unavailable right now',
            description: `${errorMessage(e)} Your order is saved — pay from the order page or your email.`,
            variant: 'destructive',
          });
          setStep('done');
          return;
        }
      }
      // Cash on delivery has no pay step — the owner collects on handover.
      setStep(method === 'cash' ? 'done' : 'pay');
    } catch (e) {
      toast({ title: 'Could not place order', description: errorMessage(e), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  }

  async function claimPaid() {
    if (!placed) return;
    setSubmitting(true);
    try {
      await fetch('/api/orders/notify', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ order_id: placed.id, event: 'claimed' }),
      });
    } catch {
      /* best-effort */
    }
    onPaymentClaimed?.(placed.id);
    setSubmitting(false);
    setStep('done');
  }

  function close() {
    // Leaving mid-payment is fine — the confirmation email carries the link.
    if (step === 'done') setStep('details');
    onClose();
  }

  // The sequence number comes back from /api/orders/notify. If that call
  // fails (offline, dev server), fall back to a short slice of the order id
  // — the owner's Orders drawer shows the same ref, so it still matches.
  const orderRef = placed ? (placed.orderNumber ? `#${placed.orderNumber}` : shortOrderRef(placed.id)) : '';
  const memo = placed ? `Order ${orderRef} — ${card.display_name}` : '';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.button
            aria-label="Close"
            onClick={close}
            className="absolute inset-0 bg-ink/60 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={SPRING_DEFAULT}
            className="relative flex max-h-[92dvh] w-full max-w-md flex-col rounded-t-2xl bg-bone text-ink safe-pb"
          >
            <div className="flex items-center justify-between px-6 pt-5">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-muted">
                  {step === 'details' ? 'Your order' : step === 'pay' ? 'Payment' : 'All set'}
                </p>
                <h2 className="mt-1 text-xl font-medium tracking-tight">
                  {step === 'details'
                    ? `Order from ${card.display_name.split(' ')[0]}`
                    : step === 'pay'
                      ? `Pay ${formatMoney(placed?.amountCents ?? 0, currency)}`
                      : 'Thank you'}
                </h2>
              </div>
              <button
                onClick={close}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-full text-ink-muted transition-colors hover:bg-bone-warm hover:text-ink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4">
              {step === 'details' && (
                <form onSubmit={handleSubmit(placeOrder)} className="space-y-5">
                  {/* Lines */}
                  {lines.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-bone-deep p-6 text-center">
                      <ShoppingBag className="mx-auto h-5 w-5 text-ink-muted" />
                      <p className="mt-2 text-sm text-ink-muted">Your order is empty.</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-bone-deep overflow-hidden rounded-xl border border-bone-deep bg-surface">
                      {lines.map((l) => (
                        <li key={l.key} className="flex items-center gap-3 p-3">
                          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-bone-warm">
                            {l.product.imageUrl ? (
                              <img src={l.product.imageUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-ink-muted">
                                <ShoppingBag className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="line-clamp-2 text-sm font-medium leading-snug tracking-tight">{l.product.name}</p>
                            {formatOptions(l.options) && (
                              <p className="truncate text-[11px] text-ink-soft">{formatOptions(l.options)}</p>
                            )}
                            <p className="text-xs text-ink-muted">
                              {formatMoney(l.product.priceCents, currency)} each
                            </p>
                          </div>
                          <QtyStepper
                            qty={l.qty}
                            onChange={(q) => cart.setQty(l.key, q)}
                          />
                          <p className="w-16 text-right text-sm font-medium tabular-nums">
                            {formatMoney(l.product.priceCents * l.qty, currency)}
                          </p>
                        </li>
                      ))}
                      <li className="flex items-center justify-between bg-bone-warm/60 px-3 py-2.5">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">
                          Total
                        </span>
                        <span className="text-base font-medium tabular-nums">
                          {formatMoney(subtotal, currency)}
                        </span>
                      </li>
                    </ul>
                  )}

                  {/* Contact */}
                  <div className="grid gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="co-name">Your name</Label>
                      <Input id="co-name" autoComplete="name" {...register('name')} placeholder="Jane Doe" />
                      {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="co-email">Email</Label>
                      <Input
                        id="co-email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        {...register('email')}
                        placeholder="you@domain.com"
                      />
                      {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="co-phone">Phone (optional)</Label>
                      <Input
                        id="co-phone"
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        {...register('phone')}
                        placeholder="+1 555 000 0000"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="co-note">Note (optional)</Label>
                      <Textarea
                        id="co-note"
                        rows={2}
                        className="min-h-[64px]"
                        {...register('note')}
                        placeholder="Size, color, pickup time…"
                      />
                    </div>
                  </div>

                  {/* Payment method */}
                  <div className="space-y-2">
                    <Label>Pay with</Label>
                    {methods.length === 0 ? (
                      <p className="rounded-lg border border-bone-deep bg-bone-warm px-3 py-2 text-xs text-ink-muted">
                        {card.display_name} hasn't switched on a payment method yet.
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {methods.map((m) => {
                          const meta = PAYMENT_META[m];
                          const active = method === m;
                          return (
                            <button
                              key={m}
                              type="button"
                              onClick={() => setMethod(m)}
                              aria-pressed={active}
                              className={cn(
                                'flex h-11 items-center gap-2 rounded-md border px-3 text-sm font-medium tracking-tight transition-colors',
                                active
                                  ? 'border-ink bg-ink text-bone'
                                  : 'border-bone-deep bg-surface text-ink hover:bg-bone-warm',
                              )}
                            >
                              <span
                                className="h-2.5 w-2.5 shrink-0 rounded-full"
                                style={{ backgroundColor: meta.color }}
                              />
                              {m === 'stripe' ? 'Card / Apple Pay' : (meta.shortName ?? meta.name)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {config.instructions && (
                      <p className="text-xs leading-relaxed text-ink-muted">{config.instructions}</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={submitting || lines.length === 0 || !method}
                  >
                    {submitting
                      ? method === 'stripe'
                        ? 'Opening secure checkout…'
                        : 'Placing order…'
                      : method === 'stripe'
                        ? `Pay ${formatMoney(subtotal, currency)} by card`
                        : `Place order · ${formatMoney(subtotal, currency)}`}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <p className="text-center text-[10px] leading-relaxed text-ink-muted">
                    {method === 'stripe'
                      ? 'Secure checkout by Stripe — cards, Apple Pay, Google Pay. Your order confirms instantly.'
                      : method === 'cash'
                        ? `Nothing to pay now. Have ${formatMoney(subtotal, currency)} in cash ready when ${card.display_name.split(' ')[0]} hands over your order.`
                        : `You'll pay on the next step. ${card.display_name.split(' ')[0]} confirms your payment by hand and emails you.`}
                  </p>
                </form>
              )}

              {step === 'pay' && placed && (
                <PayStep
                  placed={placed}
                  config={config}
                  currency={currency}
                  memo={memo}
                  orderRef={orderRef}
                  ownerName={card.display_name}
                  submitting={submitting}
                  onClaim={claimPaid}
                />
              )}

              {step === 'done' && placed && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center gap-3 py-6 text-center"
                >
                  <div
                    className="flex h-12 w-12 items-center justify-center rounded-full"
                    style={{ backgroundColor: theme.accent }}
                  >
                    <Check className="h-5 w-5 text-bone" />
                  </div>
                  <p className="text-base font-medium tracking-tight">
                    Order {orderRef} is in.
                  </p>
                  <p className="max-w-xs text-xs leading-relaxed text-ink-muted">
                    {placed.method === 'cash' ? (
                      <>
                        Have <span className="text-ink">{formatMoney(placed.amountCents, currency)}</span> in
                        cash ready when your order is handed over. {card.display_name.split(' ')[0]} will
                        follow up at <span className="text-ink">{placed.email}</span>.
                      </>
                    ) : (
                      <>
                        {card.display_name.split(' ')[0]} will confirm the payment and follow up at{' '}
                        <span className="text-ink">{placed.email}</span>.
                      </>
                    )}
                  </p>
                  {placed.method === 'cash' && config.cash?.note && (
                    <p className="max-w-xs rounded-lg border border-bone-deep bg-surface px-3 py-2 text-xs leading-relaxed text-ink-soft">
                      {config.cash.note}
                    </p>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <a
                      href={`/o/${placed.id}`}
                      className="inline-flex h-9 items-center gap-1.5 rounded-md border border-bone-deep px-3 text-xs font-medium text-ink hover:bg-bone-warm"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Track order
                    </a>
                    <Button onClick={close} size="sm">
                      Done
                    </Button>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Pay step ─────────────────────────────────────────────────────────────────
function PayStep({
  placed,
  config,
  currency,
  memo,
  orderRef,
  ownerName,
  submitting,
  onClaim,
}: {
  placed: PlacedOrder;
  config: PaymentConfig;
  currency: string;
  memo: string;
  orderRef: string;
  ownerName: string;
  submitting: boolean;
  onClaim: () => void;
}) {
  const meta = PAYMENT_META[placed.method];
  const target = payTarget(placed.method, config, placed.amountCents, memo);
  const [opened, setOpened] = useState(false);

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-bone-deep bg-surface p-4">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">
            Order {orderRef}
          </span>
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium"
            style={{ backgroundColor: meta.color + '18', color: meta.color }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
            {meta.name}
          </span>
        </div>
        <p className="mt-2 text-3xl font-medium tracking-tightest tabular-nums">
          {formatMoney(placed.amountCents, currency)}
        </p>
        <p className="mt-1 text-xs text-ink-muted">to {ownerName}</p>
      </div>

      {placed.method === 'zelle' ? (
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-ink-soft">
            Open your banking app, choose <span className="font-medium text-ink">Zelle</span>, and
            send {formatMoney(placed.amountCents, currency)} to:
          </p>
          <CopyRow label={config.zelle?.name ? `${config.zelle.name} · Zelle` : 'Zelle contact'} value={target.display} />
          <CopyRow label="Memo" value={memo} />
        </div>
      ) : (
        <div className="space-y-3">
          <a
            href={target.href ?? '#'}
            target="_blank"
            rel="noreferrer noopener"
            onClick={() => setOpened(true)}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full text-sm font-medium tracking-tight text-bone transition-transform active:scale-[0.98]"
            style={{ backgroundColor: meta.color }}
          >
            <ExternalLink className="h-4 w-4" />
            {target.label}
          </a>
          <CopyRow label={`${meta.name} · ${target.display}`} value={target.display} compact />
          <CopyRow label="Memo — include this so your order can be matched" value={memo} />
          {placed.method === 'venmo' && target.webHref && (
            <p className="text-[11px] text-ink-muted">
              The button opens the Venmo app when installed. No app?{' '}
              <a href={target.webHref} target="_blank" rel="noreferrer noopener" className="underline underline-offset-2">
                Pay on the web
              </a>
              .
            </p>
          )}
        </div>
      )}

      <Button
        onClick={onClaim}
        size="lg"
        className="w-full"
        disabled={submitting}
        variant={opened || placed.method === 'zelle' ? 'default' : 'outline'}
      >
        <Check className="h-4 w-4" />
        {submitting ? 'Sending…' : "I've sent the payment"}
      </Button>
      <p className="text-center text-[10px] leading-relaxed text-ink-muted">
        We've emailed you these instructions too, so you can pay later if you need to.{' '}
        <a href={`/o/${placed.id}`} className="underline underline-offset-2">Track this order</a>.
      </p>
    </div>
  );
}

/** Uppercase first 8 hex chars of the uuid — human-readable fallback ref. */
export function shortOrderRef(id: string): string {
  return id.replace(/-/g, '').slice(0, 8).toUpperCase();
}

function CopyRow({ label, value, compact }: { label: string; value: string; compact?: boolean }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({ title: 'Could not copy', variant: 'destructive' });
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border border-bone-deep bg-surface text-left transition-colors hover:bg-bone-warm',
        compact ? 'px-3 py-2' : 'px-3 py-2.5',
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="font-mono text-[9px] uppercase tracking-widest text-ink-muted">{label}</p>
        <p className={cn('truncate font-mono text-ink', compact ? 'text-xs' : 'text-sm')}>{value}</p>
      </div>
      {copied ? (
        <Check className="h-4 w-4 shrink-0 text-accent" />
      ) : (
        <Copy className="h-4 w-4 shrink-0 text-ink-muted" />
      )}
    </button>
  );
}

function QtyStepper({ qty, onChange }: { qty: number; onChange: (q: number) => void }) {
  return (
    <div className="flex items-center rounded-full border border-bone-deep bg-bone">
      <button
        type="button"
        onClick={() => onChange(qty - 1)}
        aria-label={qty === 1 ? 'Remove' : 'Decrease quantity'}
        className="flex h-8 w-8 items-center justify-center text-ink-muted hover:text-ink"
      >
        {qty === 1 ? <Trash2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
      </button>
      <span className="w-6 text-center text-sm font-medium tabular-nums">{qty}</span>
      <button
        type="button"
        onClick={() => onChange(qty + 1)}
        aria-label="Increase quantity"
        className="flex h-8 w-8 items-center justify-center text-ink-muted hover:text-ink"
      >
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
